/**
 * Device fingerprint — port of ``ando-frontend/app/getDeviceId.ts`` (v1).
 *
 * The v1 implementation pulls in ``@fingerprintjs/fingerprintjs`` (~15 kB
 * gzipped) on the server. For v2 we keep the FingerprintJS contract (same
 * ``visitorId`` shape) but defer the heavy import behind a dynamic
 * ``import()`` so the web bundle stays slim on routes that don't need it.
 *
 * Fallback chain:
 *   1. Cached value in ``AndoStorage`` under ``"device_id"``.
 *   2. FingerprintJS visitor ID, if the package is installed.
 *   3. Web Crypto random UUID — good enough for non-cryptographic
 *      identification (the BE re-validates against the session anyway).
 *
 * The fingerprint is consumed by the WS gateway (cookie ``device_id``) and
 * by the audit log on every action proposal.
 */

import { getStorage } from "../storage/index.js";

const STORAGE_KEY = "device_id";

let inflight: Promise<string> | null = null;

export async function getDeviceId(): Promise<string> {
  if (inflight) return inflight;
  inflight = (async () => {
    const storage = getStorage();
    const cached = await storage.get<string>(STORAGE_KEY);
    if (typeof cached === "string" && cached.length > 0) return cached;

    const next = await resolveFingerprint();
    await storage.set<string>(STORAGE_KEY, next);
    return next;
  })();
  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

async function resolveFingerprint(): Promise<string> {
  // Prefer FingerprintJS when available — same visitorId we shipped in v1.
  // Module is an optional dependency: dynamic import path is a string variable
  // so TypeScript doesn't try to resolve it at build time. Add the dep with
  // `pnpm add -D @fingerprintjs/fingerprintjs --filter @ando/core` if needed.
  try {
    type FpAgent = { get: () => Promise<{ visitorId: string }> };
    type FpModule = { default: { load: () => Promise<FpAgent> } };
    const moduleId = "@fingerprintjs/fingerprintjs";
    const mod = (await import(/* @vite-ignore */ /* webpackIgnore: true */ moduleId)) as FpModule;
    const agent = await mod.default.load();
    const result = await agent.get();
    if (result.visitorId) return result.visitorId;
  } catch {
    // Not installed or failed to load — fall through to crypto.randomUUID.
  }

  if (typeof globalThis.crypto !== "undefined" && "randomUUID" in globalThis.crypto) {
    return globalThis.crypto.randomUUID();
  }
  // Last-ditch fallback for ancient runtimes.
  return `device-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Test hook — clear the cached fingerprint. */
export async function __resetDeviceIdForTests(): Promise<void> {
  await getStorage().remove(STORAGE_KEY);
}
